import type {
  Assessment,
  AssessmentDataPoint,
  ClinicalNote,
  Medication,
  TreatmentPlan,
} from '../../clients/data/types';

// ---------------------------------------------------------------------------
// Session-specific types
// ---------------------------------------------------------------------------

export interface SessionClient {
  id: string;
  name: string;
  initials: string;
  pronouns: string;
  age: number;
  dob: string;
  primaryDiagnosis: { code: string; description: string };
  secondaryDiagnoses?: { code: string; description: string }[];
  insurance: { payer: string; memberId: string; plan: string };
  emergencyContact: { name: string; relationship: string; phone: string };
  alerts: { level: 'info' | 'warning' | 'critical'; message: string }[];
  episodeStatus: string;
  provider: string;
}

import type { CalendarClientAttendanceStatus } from '@/lib/calendarClientAttendanceStatus';

export interface GroupParticipant {
  id: string;
  name: string;
  initials: string;
  /** Calendar-aligned attendance (Appointment participant extension). */
  status: CalendarClientAttendanceStatus | null;
  note?: string;
}

export type AttendanceStatus = 'present' | 'absent' | 'late' | 'excused' | 'cancelled';

export interface SessionAppointment {
  id: string;
  sessionType: 'individual' | 'group';
  scheduledTime: string;
  duration: number;
  modality: 'telehealth';
  client: SessionClient;
  groupRoster?: GroupParticipant[];
}

export interface ProtocolSection {
  id: string;
  title: string;
  category: 'risk-assessment' | 'crisis-management' | 'safety-planning' | 'clinical-protocol';
  steps: { label: string; detail: string }[];
  alerts?: { severity: 'info' | 'warning' | 'critical'; message: string }[];
}

export interface AssessmentQuestion {
  id: string;
  text: string;
  options: { value: number; label: string }[];
}

export interface AssessmentTemplate {
  type: string;
  label: string;
  maxScore: number;
  questions: AssessmentQuestion[];
  severityBands: { min: number; max: number; label: string; color: string }[];
}

// ---------------------------------------------------------------------------
// Mock client
// ---------------------------------------------------------------------------

export const mockSessionClient: SessionClient = {
  id: 'client-001',
  name: 'Sarah Johnson',
  initials: 'SJ',
  pronouns: 'she/her',
  age: 34,
  dob: '1991-08-14',
  primaryDiagnosis: { code: 'F41.1', description: 'Generalized Anxiety Disorder' },
  secondaryDiagnoses: [
    { code: 'F33.1', description: 'Major Depressive Disorder, recurrent, moderate' },
  ],
  insurance: { payer: 'Blue Shield of California', memberId: 'BSC-8829174', plan: 'PPO' },
  emergencyContact: { name: 'Michael Johnson', relationship: 'Spouse', phone: '(415) 555-0142' },
  alerts: [
    { level: 'warning', message: 'History of suicidal ideation — last screened 2026-02-10' },
    { level: 'info', message: 'Prefers grounding techniques over exposure exercises' },
  ],
  episodeStatus: 'Active',
  provider: 'Dr. Emily Chen',
};

// ---------------------------------------------------------------------------
// Mock group roster
// ---------------------------------------------------------------------------

export const mockGroupRoster: GroupParticipant[] = [
  { id: 'gp-1', name: 'Sarah Johnson', initials: 'SJ', status: null },
  { id: 'gp-2', name: 'Marcus Williams', initials: 'MW', status: null },
  { id: 'gp-3', name: 'Priya Patel', initials: 'PP', status: null },
  { id: 'gp-4', name: 'James O\'Brien', initials: 'JO', status: null },
  { id: 'gp-5', name: 'Aisha Thompson', initials: 'AT', status: null },
  { id: 'gp-6', name: 'David Kim', initials: 'DK', status: null },
];

// ---------------------------------------------------------------------------
// Mock appointment
// ---------------------------------------------------------------------------

export const mockSessionAppointment: SessionAppointment = {
  id: 'appt-tele-001',
  sessionType: 'individual',
  scheduledTime: '2026-04-07T14:00:00',
  duration: 50,
  modality: 'telehealth',
  client: mockSessionClient,
  groupRoster: mockGroupRoster,
};

// ---------------------------------------------------------------------------
// Mock clinical notes
// ---------------------------------------------------------------------------

export const mockSessionNotes: ClinicalNote[] = [
  {
    id: 'note-001',
    type: 'Progress Note',
    noteType: 'Progress Note',
    date: '2026-03-31',
    dateOfService: '2026-03-31',
    provider: 'Dr. Emily Chen',
    providerType: 'Psychologist',
    serviceType: 'Individual Therapy',
    appointmentType: 'Follow-up',
    serviceRendered: '90837',
    title: 'Individual Therapy — Follow-up',
    status: 'Completed',
    noteStatus: 'Signed',
    billingStatus: 'Billed',
    appointmentId: 'appt-prev-001',
    cptCodes: ['90837'],
    soapNote: {
      subjective: 'Client reports continued difficulty with sleep onset. Describes racing thoughts at night focused on work deadlines. States anxiety has been "about a 7 out of 10" this past week. Reports using breathing exercises 3-4 times but with limited effect.',
      objective: 'Client appeared mildly anxious, fidgeting with hands. Speech rate slightly elevated. Affect congruent with reported mood. GAD-7 score: 14 (moderate). PHQ-9 score: 11 (moderate).',
      assessment: 'Generalized Anxiety Disorder symptoms remain moderate. Sleep disturbance is exacerbating daytime anxiety. Current coping strategies insufficient for nighttime rumination. Client is engaged and motivated for treatment.',
      plan: 'Introduce progressive muscle relaxation for sleep onset. Continue CBT focus on cognitive restructuring of work-related catastrophic thinking. Consider sleep hygiene psychoeducation next session. Continue current medication regimen.',
    },
    signedBy: 'Dr. Emily Chen',
    signedDate: '2026-03-31',
  },
  {
    id: 'note-002',
    type: 'Progress Note',
    noteType: 'Progress Note',
    date: '2026-03-24',
    dateOfService: '2026-03-24',
    provider: 'Dr. Emily Chen',
    providerType: 'Psychologist',
    serviceType: 'Individual Therapy',
    appointmentType: 'Follow-up',
    serviceRendered: '90837',
    title: 'Individual Therapy — Follow-up',
    status: 'Completed',
    noteStatus: 'Signed',
    billingStatus: 'Billed',
    appointmentId: 'appt-prev-002',
    cptCodes: ['90837'],
    soapNote: {
      subjective: 'Client describes improvement in workplace anxiety after practicing thought records. Reports one panic episode mid-week triggered by an unexpected meeting. States she was able to use grounding techniques to manage it within 10 minutes.',
      objective: 'Client calm and engaged. Eye contact appropriate. No psychomotor agitation observed. PHQ-9: 10 (moderate). Client brought completed thought records.',
      assessment: 'Progress noted in applying CBT techniques in real-time. Panic episode was self-managed successfully, indicating growing self-efficacy. Moderate depressive symptoms persist but are stable.',
      plan: 'Review and reinforce thought record practice. Begin behavioral activation scheduling for depressive symptoms. Assign pleasant activity log for next session.',
    },
    signedBy: 'Dr. Emily Chen',
    signedDate: '2026-03-24',
  },
  {
    id: 'note-003',
    type: 'Intake Note',
    noteType: 'Intake Note',
    date: '2026-01-15',
    dateOfService: '2026-01-15',
    provider: 'Dr. Emily Chen',
    providerType: 'Psychologist',
    serviceType: 'Individual Therapy',
    appointmentType: 'Initial Evaluation',
    serviceRendered: '90791',
    title: 'Intake Evaluation',
    status: 'Completed',
    noteStatus: 'Signed',
    billingStatus: 'Billed',
    appointmentId: 'appt-intake',
    cptCodes: ['90791'],
    soapNote: {
      subjective: 'Client is a 34-year-old female presenting with complaints of persistent worry, difficulty concentrating, and sleep disturbance over the past 8 months. Reports increased work pressure as a trigger. Denies substance use. Reports history of depression in her 20s, treated with SSRIs.',
      objective: 'Alert and oriented x4. Mildly anxious affect. Speech normal rate and rhythm. Thought process linear and goal-directed. Denies SI/HI. GAD-7: 16 (severe). PHQ-9: 13 (moderate).',
      assessment: 'Meets criteria for Generalized Anxiety Disorder (F41.1) and Major Depressive Disorder, recurrent, moderate (F33.1). Good insight and motivation for treatment.',
      plan: 'Begin weekly individual CBT. Coordinate with psychiatrist Dr. Ramirez regarding medication evaluation. Establish safety plan given history. Initial treatment plan to be completed.',
    },
    signedBy: 'Dr. Emily Chen',
    signedDate: '2026-01-15',
  },
  {
    id: 'note-004',
    type: 'Risk Assessment',
    noteType: 'Risk Assessment',
    date: '2026-02-10',
    dateOfService: '2026-02-10',
    provider: 'Dr. Emily Chen',
    providerType: 'Psychologist',
    serviceType: 'Individual Therapy',
    appointmentType: 'Follow-up',
    serviceRendered: '90837',
    title: 'Risk Assessment — Routine Screening',
    status: 'Completed',
    noteStatus: 'Signed',
    billingStatus: 'N/A',
    soapNote: {
      subjective: 'Client denies current suicidal ideation. Reports passive thoughts of "not wanting to exist" approximately 2 weeks ago during high stress period but states these resolved. No plan or intent. Reports safety plan is accessible.',
      objective: 'Columbia Suicide Severity Rating Scale administered. Severity: 1 (Wish to be dead). No active ideation, plan, or intent. Protective factors: spouse, employment, treatment engagement.',
      assessment: 'Low acute risk. Chronic risk factors include history of depression and passive ideation. Client demonstrates appropriate help-seeking behavior.',
      plan: 'Continue monitoring. Safety plan reviewed and updated. Next formal risk screening in 90 days or sooner if clinically indicated.',
    },
    signedBy: 'Dr. Emily Chen',
    signedDate: '2026-02-10',
  },
  {
    id: 'note-005',
    type: 'Psychotherapy Note',
    noteType: 'Psychotherapy Note',
    date: '2026-03-17',
    dateOfService: '2026-03-17',
    provider: 'Dr. Emily Chen',
    providerType: 'Psychologist',
    serviceType: 'Individual Therapy',
    appointmentType: 'Follow-up',
    serviceRendered: '90837',
    title: 'Psychotherapy Note — Session Process',
    status: 'Completed',
    noteStatus: 'Private',
    billingStatus: 'N/A',
  },
  {
    id: 'note-006',
    type: 'Progress Note',
    noteType: 'Progress Note',
    date: '2026-03-10',
    dateOfService: '2026-03-10',
    provider: 'Dr. Emily Chen',
    providerType: 'Psychologist',
    serviceType: 'Individual Therapy',
    appointmentType: 'Follow-up',
    serviceRendered: '90837',
    title: 'Individual Therapy — Follow-up',
    status: 'Completed',
    noteStatus: 'Signed',
    billingStatus: 'Billed',
    appointmentId: 'appt-prev-003',
    cptCodes: ['90837'],
    soapNote: {
      subjective: 'Client reports moderate anxiety but notes she attended a social event over the weekend which she had been avoiding. Describes feeling "proud but exhausted."',
      objective: 'Affect brighter than previous session. GAD-7: 12 (moderate). Reports improved appetite.',
      assessment: 'Positive behavioral progress with social engagement. Anxiety remains moderate but functional impairment decreasing.',
      plan: 'Continue graded exposure hierarchy. Process social event experience. Reinforce self-efficacy narrative.',
    },
    signedBy: 'Dr. Emily Chen',
    signedDate: '2026-03-10',
  },
];

// ---------------------------------------------------------------------------
// Mock treatment plans
// ---------------------------------------------------------------------------

export const mockSessionTreatmentPlans: TreatmentPlan[] = [
  {
    id: 'tp-001',
    program: 'Outpatient Individual Therapy',
    provider: 'Dr. Emily Chen',
    startDate: '2026-01-22',
    status: 'active',
    diagnosisCode: 'F41.1',
    diagnosisDescription: 'Generalized Anxiety Disorder',
    presentingProblem: 'Persistent worry, sleep disturbance, and difficulty concentrating interfering with occupational and social functioning.',
    behavioralDefinitions: [
      'Excessive worry about work performance and finances occurring more days than not',
      'Sleep onset insomnia (>45 min to fall asleep) 5+ nights per week',
      'Avoidance of social situations due to anticipatory anxiety',
    ],
    goals: [
      {
        id: 'g-1',
        description: 'Reduce generalized anxiety symptoms to mild range (GAD-7 < 10)',
        targetDate: '2026-07-22',
        status: 'in-progress',
        objectiveBlocks: [
          {
            id: 'ob-1',
            objective: { id: 'obj-1', description: 'Client will identify and challenge 3 automatic negative thoughts per week using thought records', targetDate: '2026-04-22', status: 'in-progress' },
            interventions: [
              'CBT: Teach cognitive restructuring techniques',
              'Assign thought record homework between sessions',
              'Review and process thought records in session',
            ],
          },
          {
            id: 'ob-2',
            objective: { id: 'obj-2', description: 'Client will practice relaxation techniques (PMR, diaphragmatic breathing) daily', targetDate: '2026-04-22', status: 'in-progress' },
            interventions: [
              'Teach progressive muscle relaxation (PMR)',
              'Introduce diaphragmatic breathing exercises',
              'Assign daily relaxation practice log',
            ],
          },
        ],
      },
      {
        id: 'g-2',
        description: 'Improve sleep quality — reduce sleep onset to < 30 minutes on 5+ nights/week',
        targetDate: '2026-07-22',
        status: 'in-progress',
        objectiveBlocks: [
          {
            id: 'ob-3',
            objective: { id: 'obj-3', description: 'Client will implement sleep hygiene protocol consistently for 4+ weeks', targetDate: '2026-05-22', status: 'not-started' },
            interventions: [
              'Provide sleep hygiene psychoeducation',
              'Develop personalized wind-down routine',
              'Monitor with sleep diary',
            ],
          },
        ],
      },
      {
        id: 'g-3',
        description: 'Increase social engagement — attend 2+ social activities per month without avoidance',
        targetDate: '2026-10-22',
        status: 'in-progress',
        objectiveBlocks: [
          {
            id: 'ob-4',
            objective: { id: 'obj-4', description: 'Client will complete graded exposure hierarchy for social situations', targetDate: '2026-07-22', status: 'in-progress' },
            interventions: [
              'Develop graded exposure hierarchy collaboratively',
              'Process exposure experiences in session',
              'Reinforce approach behaviors and self-efficacy',
            ],
          },
        ],
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Mock assessments
// ---------------------------------------------------------------------------

export const mockSessionAssessments: Assessment[] = [
  { id: 'a-1', assessmentType: 'PHQ-9', date: '2026-03-31', time: '14:15', provider: 'Dr. Emily Chen', score: 11, maxScore: 27, severity: 'Moderate', administeredBy: 'Dr. Emily Chen', status: 'Completed' },
  { id: 'a-2', assessmentType: 'GAD-7', date: '2026-03-31', time: '14:20', provider: 'Dr. Emily Chen', score: 14, maxScore: 21, severity: 'Moderate', administeredBy: 'Dr. Emily Chen', status: 'Completed' },
  { id: 'a-3', assessmentType: 'PHQ-9', date: '2026-03-17', time: '14:10', provider: 'Dr. Emily Chen', score: 10, maxScore: 27, severity: 'Moderate', administeredBy: 'Dr. Emily Chen', status: 'Completed' },
  { id: 'a-4', assessmentType: 'GAD-7', date: '2026-03-17', time: '14:15', provider: 'Dr. Emily Chen', score: 12, maxScore: 21, severity: 'Moderate', administeredBy: 'Dr. Emily Chen', status: 'Completed' },
  { id: 'a-5', assessmentType: 'PHQ-9', date: '2026-03-03', time: '14:10', provider: 'Dr. Emily Chen', score: 12, maxScore: 27, severity: 'Moderate', administeredBy: 'Dr. Emily Chen', status: 'Completed' },
  { id: 'a-6', assessmentType: 'GAD-7', date: '2026-03-03', time: '14:15', provider: 'Dr. Emily Chen', score: 15, maxScore: 21, severity: 'Moderate-Severe', administeredBy: 'Dr. Emily Chen', status: 'Completed' },
  { id: 'a-7', assessmentType: 'PHQ-9', date: '2026-01-15', time: '10:30', provider: 'Dr. Emily Chen', score: 13, maxScore: 27, severity: 'Moderate', administeredBy: 'Dr. Emily Chen', status: 'Completed' },
  { id: 'a-8', assessmentType: 'GAD-7', date: '2026-01-15', time: '10:35', provider: 'Dr. Emily Chen', score: 16, maxScore: 21, severity: 'Severe', administeredBy: 'Dr. Emily Chen', status: 'Completed' },
];

export const mockAssessmentTrends: AssessmentDataPoint[] = [
  { date: '2026-01-15', session: 'Intake', 'PHQ-9': 13, 'GAD-7': 16 },
  { date: '2026-02-03', session: 'Session 3', 'PHQ-9': 12, 'GAD-7': 15 },
  { date: '2026-02-17', session: 'Session 5', 'PHQ-9': 11, 'GAD-7': 14 },
  { date: '2026-03-03', session: 'Session 7', 'PHQ-9': 12, 'GAD-7': 15 },
  { date: '2026-03-17', session: 'Session 9', 'PHQ-9': 10, 'GAD-7': 12 },
  { date: '2026-03-31', session: 'Session 11', 'PHQ-9': 11, 'GAD-7': 14 },
];

// ---------------------------------------------------------------------------
// Assessment question banks for in-session administration
// ---------------------------------------------------------------------------

const phq9Questions: AssessmentQuestion[] = [
  { id: 'phq-1', text: 'Little interest or pleasure in doing things', options: [{ value: 0, label: 'Not at all' }, { value: 1, label: 'Several days' }, { value: 2, label: 'More than half the days' }, { value: 3, label: 'Nearly every day' }] },
  { id: 'phq-2', text: 'Feeling down, depressed, or hopeless', options: [{ value: 0, label: 'Not at all' }, { value: 1, label: 'Several days' }, { value: 2, label: 'More than half the days' }, { value: 3, label: 'Nearly every day' }] },
  { id: 'phq-3', text: 'Trouble falling or staying asleep, or sleeping too much', options: [{ value: 0, label: 'Not at all' }, { value: 1, label: 'Several days' }, { value: 2, label: 'More than half the days' }, { value: 3, label: 'Nearly every day' }] },
  { id: 'phq-4', text: 'Feeling tired or having little energy', options: [{ value: 0, label: 'Not at all' }, { value: 1, label: 'Several days' }, { value: 2, label: 'More than half the days' }, { value: 3, label: 'Nearly every day' }] },
  { id: 'phq-5', text: 'Poor appetite or overeating', options: [{ value: 0, label: 'Not at all' }, { value: 1, label: 'Several days' }, { value: 2, label: 'More than half the days' }, { value: 3, label: 'Nearly every day' }] },
  { id: 'phq-6', text: 'Feeling bad about yourself — or that you are a failure or have let yourself or your family down', options: [{ value: 0, label: 'Not at all' }, { value: 1, label: 'Several days' }, { value: 2, label: 'More than half the days' }, { value: 3, label: 'Nearly every day' }] },
  { id: 'phq-7', text: 'Trouble concentrating on things, such as reading the newspaper or watching television', options: [{ value: 0, label: 'Not at all' }, { value: 1, label: 'Several days' }, { value: 2, label: 'More than half the days' }, { value: 3, label: 'Nearly every day' }] },
  { id: 'phq-8', text: 'Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving around a lot more than usual', options: [{ value: 0, label: 'Not at all' }, { value: 1, label: 'Several days' }, { value: 2, label: 'More than half the days' }, { value: 3, label: 'Nearly every day' }] },
  { id: 'phq-9', text: 'Thoughts that you would be better off dead or of hurting yourself in some way', options: [{ value: 0, label: 'Not at all' }, { value: 1, label: 'Several days' }, { value: 2, label: 'More than half the days' }, { value: 3, label: 'Nearly every day' }] },
];

const gad7Questions: AssessmentQuestion[] = [
  { id: 'gad-1', text: 'Feeling nervous, anxious, or on edge', options: [{ value: 0, label: 'Not at all' }, { value: 1, label: 'Several days' }, { value: 2, label: 'More than half the days' }, { value: 3, label: 'Nearly every day' }] },
  { id: 'gad-2', text: 'Not being able to stop or control worrying', options: [{ value: 0, label: 'Not at all' }, { value: 1, label: 'Several days' }, { value: 2, label: 'More than half the days' }, { value: 3, label: 'Nearly every day' }] },
  { id: 'gad-3', text: 'Worrying too much about different things', options: [{ value: 0, label: 'Not at all' }, { value: 1, label: 'Several days' }, { value: 2, label: 'More than half the days' }, { value: 3, label: 'Nearly every day' }] },
  { id: 'gad-4', text: 'Trouble relaxing', options: [{ value: 0, label: 'Not at all' }, { value: 1, label: 'Several days' }, { value: 2, label: 'More than half the days' }, { value: 3, label: 'Nearly every day' }] },
  { id: 'gad-5', text: 'Being so restless that it\'s hard to sit still', options: [{ value: 0, label: 'Not at all' }, { value: 1, label: 'Several days' }, { value: 2, label: 'More than half the days' }, { value: 3, label: 'Nearly every day' }] },
  { id: 'gad-6', text: 'Becoming easily annoyed or irritable', options: [{ value: 0, label: 'Not at all' }, { value: 1, label: 'Several days' }, { value: 2, label: 'More than half the days' }, { value: 3, label: 'Nearly every day' }] },
  { id: 'gad-7', text: 'Feeling afraid as if something awful might happen', options: [{ value: 0, label: 'Not at all' }, { value: 1, label: 'Several days' }, { value: 2, label: 'More than half the days' }, { value: 3, label: 'Nearly every day' }] },
];

export const assessmentTemplates: AssessmentTemplate[] = [
  {
    type: 'PHQ-9',
    label: 'PHQ-9 (Depression)',
    maxScore: 27,
    questions: phq9Questions,
    severityBands: [
      { min: 0, max: 4, label: 'Minimal', color: 'green' },
      { min: 5, max: 9, label: 'Mild', color: 'yellow' },
      { min: 10, max: 14, label: 'Moderate', color: 'orange' },
      { min: 15, max: 19, label: 'Moderately Severe', color: 'red' },
      { min: 20, max: 27, label: 'Severe', color: 'red' },
    ],
  },
  {
    type: 'GAD-7',
    label: 'GAD-7 (Anxiety)',
    maxScore: 21,
    questions: gad7Questions,
    severityBands: [
      { min: 0, max: 4, label: 'Minimal', color: 'green' },
      { min: 5, max: 9, label: 'Mild', color: 'yellow' },
      { min: 10, max: 14, label: 'Moderate', color: 'orange' },
      { min: 15, max: 21, label: 'Severe', color: 'red' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Mock medications
// ---------------------------------------------------------------------------

export const mockSessionMedications: Medication[] = [
  { id: 'med-1', name: 'Sertraline (Zoloft)', dosage: '100mg', frequency: 'Once daily', prescribedBy: 'Dr. Maria Ramirez', startDate: '2026-01-20', status: 'Active', notes: 'Increased from 50mg on 2026-02-17' },
  { id: 'med-2', name: 'Hydroxyzine (Vistaril)', dosage: '25mg', frequency: 'As needed for acute anxiety', prescribedBy: 'Dr. Maria Ramirez', startDate: '2026-01-20', status: 'Active', notes: 'Max 3x daily' },
  { id: 'med-3', name: 'Melatonin', dosage: '3mg', frequency: 'Nightly at bedtime', prescribedBy: 'Dr. Maria Ramirez', startDate: '2026-02-10', status: 'Active' },
];

// ---------------------------------------------------------------------------
// Mock protocols
// ---------------------------------------------------------------------------

export const mockProtocols: ProtocolSection[] = [
  {
    id: 'proto-risk',
    title: 'Suicide & Self-Harm Risk Assessment',
    category: 'risk-assessment',
    steps: [
      { label: 'Screen with validated tool', detail: 'Administer the Columbia Suicide Severity Rating Scale (C-SSRS) or PHQ-9 Item 9. Document the specific tool used and the client\'s responses verbatim.' },
      { label: 'Assess ideation specifics', detail: 'If screening is positive, assess: frequency, duration, controllability. Ask directly: "Are you thinking about killing yourself?" Document presence/absence of plan, means, and intent.' },
      { label: 'Classify risk level', detail: 'Low: passive ideation, no plan/intent, strong protective factors. Moderate: active ideation without plan, or passive with weakened protectors. High: active ideation with plan or intent. Imminent: immediate plan with access to means.' },
      { label: 'Determine intervention', detail: 'Low → safety plan review, increase monitoring. Moderate → safety plan update, increase session frequency, consider higher level of care. High → crisis intervention, means restriction counseling, contact emergency supports. Imminent → do not leave client alone, call 911/988, initiate involuntary hold if needed.' },
      { label: 'Document thoroughly', detail: 'Record risk factors, protective factors, clinical reasoning for risk level determination, interventions taken, and follow-up plan. Use a structured risk assessment note template.' },
    ],
    alerts: [
      { severity: 'critical', message: 'If client expresses imminent risk with plan and means: call 911 immediately. Do not end the telehealth session until emergency services confirm they are en route.' },
      { severity: 'warning', message: 'For telehealth sessions: confirm client\'s physical location and local emergency number at the start of every session.' },
    ],
  },
  {
    id: 'proto-crisis',
    title: 'Crisis Management Protocol',
    category: 'crisis-management',
    steps: [
      { label: 'Stay calm and present', detail: 'Use a steady, calm voice. Acknowledge the client\'s distress without minimizing. "I hear you, and I\'m here with you right now."' },
      { label: 'Assess immediate safety', detail: 'Determine if the client is in physical danger. Ask about location, who else is present, and access to means of harm. For telehealth: confirm address for emergency dispatch.' },
      { label: 'De-escalation techniques', detail: 'Guide grounding exercises: 5-4-3-2-1 sensory technique, box breathing (4-4-4-4), or bilateral tapping. Match the technique to what the client has practiced before.' },
      { label: 'Activate safety plan', detail: 'Walk through the client\'s existing safety plan step by step. Identify which coping strategies and contacts are available right now.' },
      { label: 'Contact emergency services if needed', detail: 'If risk is imminent: call 911 with client\'s confirmed location. If client is a danger to self or others and refuses voluntary help, initiate involuntary psychiatric hold per state law.' },
      { label: 'Post-crisis documentation', detail: 'Complete a Crisis Session Note within 24 hours. Include: presenting crisis, risk assessment, interventions used, outcome, and follow-up plan. Notify clinical supervisor per practice policy.' },
    ],
    alerts: [
      { severity: 'info', message: '988 Suicide & Crisis Lifeline: call or text 988 (available 24/7). Crisis Text Line: text HOME to 741741.' },
    ],
  },
  {
    id: 'proto-safety',
    title: 'Safety Planning Template',
    category: 'safety-planning',
    steps: [
      { label: 'Step 1: Warning signs', detail: 'Identify thoughts, images, mood, situations, or behaviors that indicate a crisis may be developing. Be specific: "When I start thinking no one would notice if I were gone" rather than "when I feel bad."' },
      { label: 'Step 2: Internal coping strategies', detail: 'List activities the client can do alone to take their mind off problems without contacting another person. Examples: exercise, journaling, listening to music, grounding techniques.' },
      { label: 'Step 3: Social contacts for distraction', detail: 'People and social settings that provide distraction. Include names and phone numbers. These are people the client can contact WITHOUT necessarily disclosing they are in crisis.' },
      { label: 'Step 4: People to ask for help', detail: 'Family members, friends, or others the client can tell they are in crisis. Include names and phone numbers. Also list clinician contact information.' },
      { label: 'Step 5: Professional and agency contacts', detail: 'Clinician: [name, phone]. Psychiatrist: [name, phone]. Local ER: [name, address, phone]. 988 Suicide & Crisis Lifeline. Crisis Text Line: text HOME to 741741.' },
      { label: 'Step 6: Making the environment safe', detail: 'Identify means restriction steps. Who can help secure or remove lethal means? Document specific actions taken (e.g., firearms stored with family member, medications secured).' },
    ],
  },
  {
    id: 'proto-clinical',
    title: 'Telehealth Clinical Protocols',
    category: 'clinical-protocol',
    steps: [
      { label: 'Session start checklist', detail: 'Confirm client identity. Verify client\'s current physical location (state). Confirm emergency contact is reachable. Verify private, confidential space on both ends. Test audio/video quality.' },
      { label: 'Informed consent reminders', detail: 'Ensure telehealth consent is on file and up to date. Remind client: sessions may be recorded for quality (if applicable). Review limits of confidentiality including mandatory reporting obligations.' },
      { label: 'Mandatory reporting obligations', detail: 'Child abuse or neglect: report to local CPS within timeframe per state law. Elder/dependent adult abuse: report to APS. Imminent danger to self or others: duty to warn/protect per Tarasoff. Document all reports and clinical reasoning.' },
      { label: 'Session documentation requirements', detail: 'Progress note must be completed within 24 hours of session. Include: date/time, duration, modality (telehealth), client\'s location, presenting concerns, interventions, response, and plan.' },
      { label: 'Technology failure protocol', detail: 'If video disconnects: attempt reconnection for 5 minutes. If unsuccessful, call client by phone to complete session or reschedule. Document the interruption and how it was handled.' },
      { label: 'Emergency protocol for telehealth', detail: 'Maintain client\'s confirmed address and local 911 number accessible at all times during session. If client becomes unresponsive or demonstrates imminent risk, call emergency services to client\'s location immediately.' },
    ],
    alerts: [
      { severity: 'warning', message: 'State licensing: verify you are licensed in the state where the client is physically located at the time of the session.' },
    ],
  },
];
